import { Box, Grid, TextField, Typography, useTheme } from '@mui/material';
import {
  Criteria,
  GetInspectionItem,
} from '../../../../../../services/apiTypes';
import CheckBox from '../../Input/CheckBox';
import NumberInput from '../../Input/NumberInput';
import React, { useEffect, useState } from 'react';
import InspectionDetailModal, { DetailItem } from './InspectionDetailModal';
import {
  updateJudgement,
  updateNumericalValue,
} from '../../../../../../stores/inspectionTypeData/slice';
import { useDispatch, useSelector } from 'react-redux';
import {
  getColorByJudgement,
  isEmpty,
  isNg,
} from '../../../../../../_utils/inspectionUtils';
import { useTranslation } from 'react-i18next';
import {
  INPUT_TYPE,
  JUDGEMENT,
} from '../../../../../../_constants/inspectionConst';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { TEXT_OVERFLOW_2LINES } from '../../../../../../_constants/styleConst';
import { RootState } from '../../../../../../stores/store';

type StandardProps = {
  typeLabel: string;
  itemDataList: GetInspectionItem[];
  beforeItemDataList: GetInspectionItem[];
  isUnfilled: boolean;
  isVisualInspection: boolean;
};

/** 標準形式のパターン(チェックボックス&数値) */
const Standard: React.FC<StandardProps> = ({
  typeLabel,
  itemDataList,
  beforeItemDataList,
  isUnfilled,
  isVisualInspection,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const { categoryId, typeId } = useSelector(
    (state: RootState) => state.selectIds,
  );
  /** 数値入力を含む項目があるか */
  const [hasNumberInType, setHasNumberInType] = useState(false);
  /** 判定基準のテキストがある項目を含むか */
  const [hasCriteriaInType, setHasCriteriaInType] = useState(false);
  /** 問題箇所(階層1)を含む項目があるか */
  const [hasDetailInType, setHasDetailInType] = useState(false);
  /** 問題箇所選択ダイアログの開閉フラグ */
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);
  /** モーダルのタイトル */
  const [title, setTitle] = useState('');
  /** 問題箇所のリスト */
  const [details, setDetails] = useState<DetailItem[]>([]);
  /**
   * 開いているダイアログのIDパス
   * path[階層0, 階層1]
   */
  const [path, setPath] = useState<string[]>([]);

  useEffect(() => {
    // 表示切替用フラグの初期化
    setHasDetailInType(
      itemDataList.some(({ itemData }) => itemData.length > 0),
    );
    setHasNumberInType(
      itemDataList.some(({ inputType }) => inputType === INPUT_TYPE.NUMBER),
    );
    setHasCriteriaInType(
      itemDataList.some(({ criteria }) => criteria.text !== ''),
    );
  }, [typeId]);

  const style = {
    label: {
      fontSize: theme.typography.caption.fontSize ?? '0.75rem',
      textAlign: 'center',
      alignSelf: 'end',
    },
    input: {
      height: '100%',
      padding: '0px',
    },
  };

  const handleOpen = (item: GetInspectionItem) => {
    setIsOpenDetailModal(true);
    setTitle(item.label);

    const newDetails: DetailItem[] = [];
    // 前回結果(階層0)
    const beforeItem = beforeItemDataList.find(
      (before0) => before0.id === item.id,
    );

    item.itemData.forEach((detail) => {
      // 前回結果(階層1)
      const beforeDetail =
        beforeItem &&
        beforeItem.itemData.find((before1) => before1.id === detail.id);

      newDetails.push({
        id: detail.id,
        label: detail.label,
        isChecked: false,
        borderColor: beforeDetail && isNg(beforeDetail.judgement) ? 'red' : '',
      });
    });
    setDetails(newDetails);
    setPath([item.id]);
  };

  const handleClose = (shouldUpdate: boolean) => {
    if (shouldUpdate) {
      const isAnyChecked = details.some((detail) => detail.isChecked);
      // 現象は空のラベルの項目になっているが、内部的には結果の判定を更新しておく
      dispatch(
        updateJudgement({
          categoryId,
          typeId,
          path,
          judgement: isAnyChecked ? JUDGEMENT.NG : JUDGEMENT.EMPTY,
        }),
      );
      details.forEach((detail) => {
        // 1つもチェックが付いてない場合は空とする
        // それ以外はチェックあり→NG、チェックなし→OKとする
        const newJudgement = isAnyChecked
          ? detail.isChecked
            ? JUDGEMENT.NG
            : JUDGEMENT.OK
          : JUDGEMENT.EMPTY;
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [...path, detail.id],
            judgement: newJudgement,
          }),
        );
      });
    } else {
      // キャンセルの場合は空にする
      const eachPath: string[] = [];
      path.forEach((path) => {
        eachPath.push(path);
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: eachPath,
            judgement: JUDGEMENT.EMPTY,
          }),
        );
      });
      details.forEach((detail) => {
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [...path, detail.id],
            judgement: JUDGEMENT.EMPTY,
          }),
        );
      });
      // 数値をnullにする
      dispatch(
        updateNumericalValue({
          categoryId,
          typeId,
          path: [path[0]],
          numericalValue: null,
        }),
      );
    }
    setIsOpenDetailModal(false);
    setPath([]);
  };

  /** 問題箇所に表示するテキスト取得 */
  const getDetailText = (detailList: GetInspectionItem[]) => {
    const labels: string[] = [];
    detailList.forEach((detail) => {
      if (isNg(detail.judgement)) {
        labels.push(detail.label);
      }
    });

    return labels.join(', ');
  };

  /** 親の検査項目のチェックボックスをOK・N/Aにした時 */
  const clearProblemCheck = (itemId: string, judgement: number) => {
    const problemData = itemDataList.find(
      (item) => item.id === itemId,
    )?.itemData;
    if (!problemData) {
      return;
    }
    problemData.forEach((problem) => {
      dispatch(
        updateJudgement({
          categoryId,
          typeId,
          path: [itemId, problem.id],
          judgement,
        }),
      );
      // 問題箇所も更新
      problem.itemData.forEach((detail) => {
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [itemId, problem.id, detail.id],
            judgement,
          }),
        );
      });
    });
  };

  /** NG詳細TextFieldのエラーチェック */
  const isTextFieldError = (item: GetInspectionItem) => {
    if (item.itemData.length > 0) {
      return (
        ((isNg(item.judgement) && getDetailText(item.itemData) === '') ||
          isEmpty(item.judgement)) &&
        isUnfilled
      );
    } else {
      return isEmpty(item.judgement) && isUnfilled;
    }
  };

  /** 判定基準のテキスト取得 */
  const getCriteriaText = (criteria: Criteria) => {
    const min = criteria.min ? criteria.min.toString() : '';
    const max = criteria.max ? criteria.max.toString() : '';
    return min === max ? '' : `${min} - ${max}`;
  };

  return (
    <Box>
      {/* ヘッダー */}
      {hasNumberInType ? (
        // 数値あり
        <Grid container>
          <Grid
            size={hasDetailInType ? 5.5 : 6}
            mb={1}
            sx={{ display: 'flex' }}
          >
            {isVisualInspection && (
              <Box sx={{ display: 'inline', mx: 1.5 }}>
                <VisibilityIcon color="primary" />
              </Box>
            )}
            <Typography
              sx={{
                ...TEXT_OVERFLOW_2LINES,
                fontSize: theme.typography.body1.fontSize ?? '0.75rem',
              }}
            >
              {typeLabel}
            </Typography>
          </Grid>
          <Grid size={1} sx={style.label}>
            {'N/A'}
          </Grid>
          <Grid size={1}>{/* 判定結果 */}</Grid>
          {hasDetailInType && (
            <Grid size={2.5} sx={style.label}>
              {t('LBL_DEFECTIVE_POSITION')}
            </Grid>
          )}
          <Grid size={hasDetailInType ? 2 : 3} sx={style.label}>
            {t('LBL_CRITERIA')}
          </Grid>
          {!hasDetailInType && <Grid size={1}>{/* 空 */}</Grid>}
        </Grid>
      ) : (
        // チェックボックスのみ
        <Grid container>
          <Grid size={6} mb={1} sx={{ display: 'flex' }}>
            {isVisualInspection && (
              <Box sx={{ display: 'inline', mx: 1.5 }}>
                <VisibilityIcon color="primary" />
              </Box>
            )}
            <Typography
              sx={[
                { fontSize: theme.typography.body1.fontSize },
                TEXT_OVERFLOW_2LINES,
              ]}
            >
              {typeLabel}
            </Typography>
          </Grid>
          <Grid size={1}>{/* チェックボックス */}</Grid>
          <Grid size={3} sx={style.label}>
            {hasDetailInType
              ? t('LBL_DEFECTIVE_POSITION')
              : hasCriteriaInType
                ? t('LBL_CRITERIA')
                : ''}
          </Grid>
          <Grid size={2} sx={style.label}>
            {hasDetailInType && hasCriteriaInType ? t('LBL_CRITERIA') : ''}
          </Grid>
        </Grid>
      )}

      {itemDataList.map((item, i) => {
        const lineStyle = {
          height: '40px',
          alignItems: 'center',
          bgcolor: i % 2 === 0 ? theme.palette.background.paper : '',
        };
        const borderColor = getColorByJudgement(
          beforeItemDataList.find((before) => before.id === item.id)?.judgement,
        );

        switch (item.inputType) {
          // チェックボックス
          case 0: {
            return (
              <Grid container key={`inline_${item.id}_${i}`} sx={lineStyle}>
                <Grid size={0.5}>{/* (空) */}</Grid>
                <Grid
                  size={hasNumberInType ? (hasDetailInType ? 6 : 6.5) : 5.5}
                  sx={{
                    ...TEXT_OVERFLOW_2LINES,
                    fontSize: theme.typography.caption.fontSize ?? '0.75rem',
                  }}
                >
                  {item.label}
                </Grid>
                <Grid size={1} textAlign="center">
                  <CheckBox
                    itemId={`inline_${i}`}
                    categoryId={categoryId}
                    typeId={typeId}
                    judgement={item.judgement}
                    path={[item.id]}
                    onOk={() => clearProblemCheck(item.id, JUDGEMENT.OK)}
                    onNg={
                      item.itemData.length > 0
                        ? () => handleOpen(item)
                        : undefined
                    }
                    onNa={() => clearProblemCheck(item.id, JUDGEMENT.NA)}
                    borderColor={borderColor}
                    isUnfilled={isUnfilled}
                  />
                </Grid>
                {/* 問題箇所 */}
                {hasDetailInType && (
                  <Grid size={hasNumberInType ? 2.5 : 3} textAlign="center">
                    {item.itemData.length > 0 && (
                      <TextField
                        value={getDetailText(item.itemData)}
                        sx={{
                          height: '35px',
                        }}
                        slotProps={{
                          input: {
                            readOnly: true,
                            style: {
                              ...style.input,
                              background: isTextFieldError(item)
                                ? theme.palette.warning.light
                                : 'white',
                            },
                          },
                        }}
                      />
                    )}
                  </Grid>
                )}
                {/* 判定基準 */}
                <Grid size={hasDetailInType ? 2 : 3} textAlign="center">
                  <Typography
                    sx={{
                      fontSize: theme.typography.caption.fontSize,
                    }}
                  >
                    {item.criteria.text}
                  </Typography>
                </Grid>
                {!hasDetailInType && (
                  <Grid size={hasNumberInType ? 1 : 2}>{/* 空 */}</Grid>
                )}
              </Grid>
            );
          }
          // 数値入力
          case 1: {
            return (
              <Grid container key={`inline_${item.id}_${i}`} sx={lineStyle}>
                <React.Fragment>
                  <Grid size={0.5}>{/* (空) */}</Grid>
                  <Grid
                    size={hasDetailInType ? 3 : 3.5}
                    sx={{
                      ...TEXT_OVERFLOW_2LINES,
                      fontSize: theme.typography.caption.fontSize ?? '0.75rem',
                    }}
                  >
                    {item.label}
                  </Grid>
                  <Grid size={4}>
                    <NumberInput
                      id={`inline_number_${i}`}
                      item={item}
                      numericalValue={item.numericalValue}
                      criteriaMin={item.criteria.min}
                      criteriaMax={item.criteria.max}
                      onNg={
                        item.itemData.length > 0
                          ? () => handleOpen(item)
                          : undefined
                      }
                      borderColor={borderColor}
                      isUnfilled={isUnfilled}
                    />
                  </Grid>
                  {/* 問題箇所のテキスト */}
                  {hasDetailInType && (
                    <Grid size={2.5} textAlign="center">
                      {item.itemData.length > 0 && (
                        <TextField
                          value={getDetailText(item.itemData)}
                          sx={{ height: '35px' }}
                          slotProps={{
                            input: {
                              readOnly: true,
                              style: {
                                height: '100%',
                                padding: '0px',
                                background:
                                  ((isNg(item.judgement) &&
                                    getDetailText(item.itemData) === '') ||
                                    isEmpty(item.judgement)) &&
                                  isUnfilled
                                    ? theme.palette.warning.light
                                    : theme.palette.background.default,
                              },
                            },
                          }}
                        />
                      )}
                    </Grid>
                  )}
                  {/* 判定基準 */}
                  <Grid size={hasDetailInType ? 2 : 3} textAlign="center">
                    <Typography
                      sx={{
                        fontSize: theme.typography.caption.fontSize,
                      }}
                    >
                      {getCriteriaText(item.criteria)}
                    </Typography>
                  </Grid>
                  {!hasDetailInType && <Grid size={1}>{/* 空 */}</Grid>}
                </React.Fragment>
              </Grid>
            );
          }
          default:
            return <React.Fragment key={i}></React.Fragment>;
        }
      })}
      <InspectionDetailModal
        isOpen={isOpenDetailModal}
        title={title}
        details={details}
        setDetails={setDetails}
        handleClose={handleClose}
      />
    </Box>
  );
};

export default Standard;
